import { FC, useContext, useEffect, useState, useRef, useMemo, RefObject, createRef, JSX } from "react";
import { Redirect, RouteComponentProps } from "react-router-dom";
import { toast } from "react-toastify";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { NewUserModel } from "@app/admin/user-administration/userModel";
import { ColumnConfig, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { Modal, ModalLink } from "@app/shared/Modals";
import { QesIcons } from "@app/shared/icons/qesIcons";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import DragAndDropFileUploader from "@app/shared/DragAndDropFileUploader";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import { UserContext } from "@app/user/UserContext";
import UserService from "@app/user/userService";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { WizardNavigationBar } from "@app/shared/qes-standards/WizardNavigationBar";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import Roles from "@app/user/roles";
import { AdminUsersContext, AdminUsersProvider } from "@app/admin/contexts/AdminUsersContext";

interface BulkUserCreateMatchProps {
  userId?: string;
}

interface BulkUserCreateRouteState {
  clientId?: number;
}

interface UserPropertyDetails {
  displayName: string;
  name: string;
}

type BulkUserCreateProps = RouteComponentProps<BulkUserCreateMatchProps, undefined, BulkUserCreateRouteState>;

const BulkUserCreate: FC<React.PropsWithChildren<BulkUserCreateProps>> = (props: BulkUserCreateProps) => {
  // this is the user operating the application, not the user being administered
  const user = useContext(UserContext);
  // and this is the user that's operating the app
  const userIsQuestAdmin = user.roles.includes(Roles.QUEST_ADMIN);
  const adminUsersContext = useContext(AdminUsersContext);
  const userService = new UserService();
  const [submitting, setSubmitting] = useState(false);
  const [redirect, setRedirect] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [errorList, setErrorList] = useState<JSX.Element[]>([]);
  const [excludeHeaderRow, setExcludeHeaderRow] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const [csvListTableData, setCsvListTableData] = useState([]);
  const [csvColumnFieldLink, setCsvColumnFieldLink] = useState([]);
  const [conflictedColumn, setConflictedColumn] = useState(null);
  const [columnRefs, setColumnRefs] = useState<Array<RefObject<HTMLSelectElement>>>([]);
  const [lastSelectedDropdown, setLastSelectedDropdown] = useState<RefObject<HTMLSelectElement>>();
  const userProperties = useRef<{ [key: string]: UserPropertyDetails }>({
    address: { displayName: "Address", name: "address" },
    city: { displayName: "City", name: "city" },
    email: { displayName: "Email", name: "email" },
    firstName: { displayName: "First Name", name: "firstName" },
    lastName: { displayName: "Last Name", name: "lastName" },
    middleInitial: { displayName: "Middle Initial", name: "middleInitial" },
    phoneNumber: { displayName: "Phone Number", name: "phoneNumber" },
    state: { displayName: "State", name: "state" },
    zipCode: { displayName: "Zip Code", name: "zipCode" },
  });

  useEffect(() => {
    if (props.match.params.userId) {
      adminUsersContext.setUserId(parseInt(props.match.params.userId, 10));
    }
  }, [props.match.params.userId]);

  useEffect(() => {
    if (userIsQuestAdmin) { // user is quest admin so set the client id to the "current" client id if there is one
      if (props.location.state?.clientId) {
        adminUsersContext.setClientId(props.location.state.clientId);
      }
    } else { // user is not quest admin, so can only work in the context of their own client
      adminUsersContext.setClientId(user.clientId);
    }
  }, []);

  const rowStartIndex = useMemo(() => {
    return (excludeHeaderRow ? 1 : 0);
  }, [excludeHeaderRow])

  useEffect(() => {
    let numDisplayRows = 6 + rowStartIndex;

    if (numDisplayRows > csvData.length)
      numDisplayRows = csvData.length

    for (let i = rowStartIndex; i < numDisplayRows; i++) {
      csvData[i][wafflesKeyColumnName] = "0";
    }

    setCsvListTableData(csvData.slice(rowStartIndex, numDisplayRows));
  }, [csvData, rowStartIndex]);

  const columnSelectOptions = useMemo(() => {
    const options = [];
    for (const prop in userProperties.current) {
      options.push(<option value={userProperties.current[prop].name} key={userProperties.current[prop].name}>{userProperties.current[prop].displayName}</option>);
    }
    return options;
  }, []);

  const columnConfig = useMemo(() => {
    const config: ColumnConfig = {};

    if (csvData.length == 0)
      return config;

    for (let i = 0; i < Object.keys(csvData[0]).length; i++) {
      config[i] = {
        displayElement:
          <QESSelect
            testId={"bulk-user-create__" + i}
            label=""
            placeholder="Select a Column"
            allowEmpty={true}
            onChange={(e) => handleColumnSelectChange(e, e.target.value, i, columnRefs[i])}
            value={(!!csvColumnFieldLink[i] ? csvColumnFieldLink[i] : "")}
            qesRef={columnRefs[i]}
          >
            {columnSelectOptions}
          </QESSelect>,
        displayText: "",
        noSort: true,
        testId: i.toString() + "z"
      }
    }

    return config;
  }, [csvData, conflictedColumn, csvColumnFieldLink]);

  const formIsValid = useMemo<boolean>(() => {
    return (csvColumnFieldLink.indexOf("email") > -1 && csvColumnFieldLink.indexOf("firstName") > -1 && csvColumnFieldLink.indexOf("lastName") > -1);
  }, [csvColumnFieldLink]);

  const handleColumnSelectChange = (e: unknown, fieldName: string, columnIndex: number, columnRef: RefObject<HTMLSelectElement>) => {
    const existingFieldIndex = csvColumnFieldLink.indexOf(fieldName);
    setLastSelectedDropdown(columnRef);
    if (existingFieldIndex > -1 && !!fieldName) {
      setConflictedColumn({
        fieldName: fieldName,
        newIndex: columnIndex,
        oldIndex: existingFieldIndex
      });
    } else {
      updateColumns([{ columnIndex, fieldName }]);
    }
  }

  const updateColumns = (columnUpdates: Array<{ columnIndex: number, fieldName: string }>) => {
    const newArr = [...csvColumnFieldLink];

    for (let i = 0; i < columnUpdates.length; i++) {
      newArr[columnUpdates[i].columnIndex] = columnUpdates[i].fieldName;
    }

    setCsvColumnFieldLink(newArr);
    setConflictedColumn(null);
  }

  const handleCreate = () => {
    setSubmitting(true);

    const users: NewUserModel[] = [];
    let hasEmailError = false;
    let hasFirstNameError = false;
    let hasLastNameError = false;

    const fieldColLink: { [key: string]: number } = {};
    for (let i = 0; i < csvColumnFieldLink.length; i++) {
      fieldColLink[csvColumnFieldLink[i]] = i;
    }

    for (let i = rowStartIndex; i < csvData.length; i++) {
      if (!hasEmailError && !validateEmail(csvData[i][fieldColLink["email"]]))
        hasEmailError = true;
      if (!hasFirstNameError && !csvData[i][fieldColLink["firstName"]])
        hasFirstNameError = true
      if (!hasLastNameError && !csvData[i][fieldColLink["lastName"]])
        hasLastNameError = true

      if (hasEmailError && hasFirstNameError && hasLastNameError)
        break;

      const userModel: NewUserModel = {
        address: (!!fieldColLink["address"] ? csvData[i][fieldColLink["address"]] : null),
        city: (!!fieldColLink["city"] ? csvData[i][fieldColLink["city"]] : null),
        clientId: props.location.state?.clientId,
        clientName: null,
        email: csvData[i][fieldColLink["email"]],
        firstName: csvData[i][fieldColLink["firstName"]],
        guestLogins: null,
        jobRole: (!!fieldColLink["JobRole"] ? csvData[i][fieldColLink["JobRole"]] : null),
        jobRoleId: null,
        jobTitle: (!!fieldColLink["JobTitle"] ? csvData[i][fieldColLink["JobTitle"]] : null),
        lastName: csvData[i][fieldColLink["lastName"]],
        middleInitial: (!!fieldColLink["middleInitial"] ? csvData[i][fieldColLink["middleInitial"]][0] : null),
        phoneNumber: (!!fieldColLink["phoneNumber"] ? csvData[i][fieldColLink["phoneNumber"]] : null),
        questViewerGuestLoginsAllowed: null,
        resourceAdminGuestLoginsAllowed: null,
        roleGroupIds: null,
        roleNames: null,
        sFContactId: null,
        state: (!!fieldColLink["state"] ? csvData[i][fieldColLink["state"]] : null),
        userName: csvData[i][fieldColLink["email"]],
        zip: (!!fieldColLink["zipCode"] ? csvData[i][fieldColLink["zipCode"]] : null),
      };
      users.push(userModel);
    }

    const errList = []
    if (hasEmailError)
      errList.push("One or more Emails are invalid. Please ensure that you've selected the correct column and that all emails in that column use one of the client's authorized domains and aren't already in use.");
    if (hasFirstNameError)
      errList.push("One or more First Names are invalid. Please ensure that you've selected the correct column and that no values in that column are blank.");
    if (hasLastNameError)
      errList.push("One or more Last Names are invalid. Please ensure that you've selected the correct column and that no values in that column are blank.");

    if (errList.length > 0) {
      setErrorMsg("Invalid Data");
      setErrorList(errList.map((x, index) => <li key={index}>{x}</li>));
      setSubmitting(false);
      return;
    }

    userService.createUsers(users, true).then(() => {
      toast(<span>Your users have been created.</span>,
        {
          containerId: "z-index-9999",
          onOpen: () => {
            handleRedirect();
          },
          type: "success",
        },
      );
    }, (error) => {
      if (!!error.responseJSON && !error.responseJSON.error) {
        const failedUsers: { [key: string]: string[] } = error.responseJSON;
        setErrorMsg("Failed to create 1 or more users (expand to see list).");
        setErrorList(
          Object.keys(failedUsers).map((key) =>
            <>
              {key}
              <ul>
                {failedUsers[key].map((x, index) => <li key={index}>{x}</li>)}
              </ul>
            </>
          )
        );
      }

      toast(<span>We encountered a problem creating your new users.</span>,
        {
          containerId: "z-index-9999",
          onClose: () => {
            setSubmitting(false);
          },
          type: "error",
        },
      );
    });
  }

  const handleCancel = (): void => {
    handleRedirect();
  }

  const handleRedirect = (): void => {
    if (props.location.state?.clientId !== undefined) {
      setRedirect(`/admin/client-administration/${props.location.state.clientId}`);
    } else {
      props.history.goBack();
      setRedirect(props.location.pathname);
    }
  }

  const onUpload = async (data: FormData): Promise<void> => {
    setErrorList([]);
    const file = (data.get("usersFile") as File).text();
    file.then(function (value) {
      parseCsv(value);
      setUploadSuccess(true);
    }, function () {
      setUploadSuccess(false);
    });
  }

  const parseCsv = (data: string): void => {
    const rows = data.split(/\r\n|\n/).map(x => x.replace(/,\s*$/, ""));

    const list = [];
    for (let i = 0; i < rows.length; i++) {
      const rowData = rows[i].split(/,(?![^"]*"(?:(?:[^"]*"){2})*[^"]*$)/);
      if (rowData.length > 0) {
        const obj: { [key: number]: string } = {};
        for (let j = 0; j < rowData.length; j++) {
          let cellVal = rowData[j];
          if (cellVal.length > 0) {
            if (cellVal[0] == '"')
              cellVal = cellVal.substring(1, cellVal.length - 1);
            if (cellVal[cellVal.length - 1] == '"')
              cellVal = cellVal.substring(cellVal.length - 2, 1);
          }

          obj[j] = cellVal;
        }

        // remove the blank rows
        if (Object.values(obj).filter(x => x).length > 0) {
          list.push(obj);
        }
      }
    }

    const newRefArray: Array<RefObject<HTMLSelectElement>> = [];
    Object.keys(list[0]).forEach((k, i) => {
      newRefArray[i] = createRef<HTMLSelectElement>();
    });
    setColumnRefs(newRefArray);
    setCsvData(list);
    setCsvColumnFieldLink([]);
  }

  const validateEmail = (email: string): boolean => {
    // This is a very basic email regex that ensures there's no @ or whitespace before the @, no extra @s, and at least two non-whitespace characters after the last .
    if (!email.match(/^[^\s@]+@([^\s@.,]+\.)+[^\s@.,]{2,}$/)) {
      return false;
    }
    // now we know it's formatted properly and can get the "domain"
    const domain = email.split("@")[1];
    return adminUsersContext.allowedDomains.includes(domain);
  }

  const noClient = !adminUsersContext.clientId || adminUsersContext.clientId <= 0;

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  return (
    <Page documentTitle="Bulk User Create - Admin">
      <Breadcrumbs template={BreadcrumbTemplateEnum.UserAdministration} items={["Bulk User Create"]} />
      <PageInner>
        <PageHeader title="Bulk User Create" iconName={QesIcons.UsersV1} disableHR={false} />
        <PageSection>
          {errorList.length > 0 ? (
            <QESAlert type="danger" collapseTitle={errorMsg} onClose={() => { setErrorList([]); }}>
              {errorList}
            </QESAlert>
          ) : null}
          <DragAndDropFileUploader
            apiKeyName="usersFile"
            title=""
            filetype=".csv"
            hasBeenSuccessful={uploadSuccess}
            onUpload={onUpload}
            testId="bulk-user-create__file-upload"
          />
          {(() => {
            if (csvData.length > 0)
              return (
                <>
                  <b>Select the User property that best matches each column of data in the preview grid</b>
                  <ul>
                    <li>It is not necessary to specify a User property for each column of data.</li>
                    <li>The properties &quot;Email&quot;, &quot;First Name&quot;, and &quot;Last Name&quot; must be specified before you can continue.</li>
                    <li>A User property cannot be associated with more than one column of data.</li>
                  </ul>
                  <QESCheckbox containerClassName="col-sm-3" label="My file has a header row" testId="bulk-user-create__exclude-header-row" onChange={(e) => setExcludeHeaderRow(e.target.checked)} checked={excludeHeaderRow} />
                  <Waffles
                    columns={columnConfig}
                    data={csvListTableData}
                    testId="bulk-user-create__abbv-list"
                  />
                </>);
            else if (uploadSuccess)
              return (<b>The uploaded file appears to be empty. Please check that you&apos;ve selected the correct file and try again.</b>);
            else
              return (<></>);
          })()}
          <Modal
            isVisible={!!conflictedColumn}
            closeModal={() => setConflictedColumn(null)}
            content={{
              body: <p className="demibold">{`${conflictedColumn ? userProperties.current[conflictedColumn.fieldName].displayName : ""} is already associated with another column. Continuing will clear that selection in favor of this one. Do you wish to continue?`}</p>,
              cancelButtonText: "Cancel",
              confirmButtonAction: () => updateColumns([
                { columnIndex: conflictedColumn.oldIndex, fieldName: "" },
                { columnIndex: conflictedColumn.newIndex, fieldName: conflictedColumn.fieldName }]),
              confirmButtonText: "Continue",
              title: "Conflicted Column",
            }}
            hideBodyScroll={true}
            triggerElement={lastSelectedDropdown}
          />
        </PageSection>
        {submitting && (
          <div className="mt-3 text-center">
            <h2>Bulk user creation is in progress. This may take a few minutes....</h2>
            <LoadingSpinner />
          </div>
        )}
      </PageInner>
      <WizardNavigationBar
        leftSide={!submitting && (
          <QESButton qesType="secondary-danger" qesTestId="bulk-user-create__cancel" onClick={handleCancel}>Cancel</QESButton>
        )}
        rightSide={adminUsersContext.client?.restrictedUserCreationEnabled ?
          (
            <ModalLink
              content={{
                body: <>This client requires proper authorization to create new users.<br />Are you sure you want to continue?</>,
                cancelButtonText: "Cancel",
                confirmButtonAction: handleCreate,
                confirmButtonText: "Create",
                title: "Create New Users"
              }}
              testId="bulk-users-create__create-modal"
              classNames="me-3 qes-button-primary"
              disabled={!formIsValid || submitting || noClient}
              hideBodyScroll={true}
            >
              Create
            </ModalLink>
          ) :
          (
            <QESButton qesType="primary" disabled={!formIsValid || submitting || noClient} qesTestId="bulk-user-create__submit" onClick={handleCreate}>Create</QESButton>
          )
        }
      />
    </Page>
  )
}

const WrappedBulkUserCreate: FC<React.PropsWithChildren<BulkUserCreateProps>> = (props: BulkUserCreateProps) => (
  <AdminUsersProvider userId={parseInt(props.match.params.userId, 10)}>
    <BulkUserCreate {...props} />
  </AdminUsersProvider>
);

export { WrappedBulkUserCreate as BulkUserCreate };
